import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {WORKFLOWS_ANALYTICS_ACTIONS, ActionOrigin} from 'constants/AnalyticsConstants';
import {isFlowchartType} from 'processes/processes-platform/FeatureOptions';
import {
  ProcessFlowchartNodeType,
  TaskNodeDetailsSidepanelContentProps,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {useAllSchemasById} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useAllSchemasById';
import {buildExecutionNodeTypeIdentifier} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/useFlowchartStateAnalytics';
import {
  ProcessTaskSchemaEditorContent,
  ProcessTaskSchemaEditorContentSections,
} from 'processes/settings/ProcessTaskSchemaEditor';
import {ProcessTaskSchema} from 'processes/settings/ProcessTaskSchemaUtils';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';
import {cacheUpdatedTaskSchemaAndUpdateNode} from './nodeUtils';

export function TaskSchemaDetailsTab(props: TaskNodeDetailsSidepanelContentProps): JSX.Element {
  const context = NodeDetailsSidepanelContext.useContext();
  // TODO(BNCH-126759): Update types to better handle when selected node is task vs logic node
  const selectedNode = context.selectedNode as TaskSchemaNode;

  const {
    processTaskSchemas: allTaskSchemas,
    storableSchemas,
    tagSchemas,
    resultSchemas,
    statusLifecycles,
    haveSchemasLoaded,
  } = useAllSchemasById(context.flowchartTaskSchema);

  const taskSchemaId = selectedNode.data.taskSchemaId;
  const baseTaskSchema = context.allSchemasById[
    taskSchemaId
  ] as ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  const taskSchema: ProcessTaskSchema = {
    ...baseTaskSchema,
    relevantForms: [],
    flowchartConfig: null,
  };

  const taskDefinitionId = context.taskDefinitionsForSidepanel.find(
    (def) => def.latestVersion?.processTaskSchema?.id === taskSchemaId
  )?.id;

  return !haveSchemasLoaded ? (
    <CenteredHorizontalThrobber />
  ) : (
    <div
      className={css(styles.taskSchemaEditorContainer)}
      {...getTestProps({component: 'TaskSchemaDetailsTab', element: 'root'})}
    >
      <ProcessTaskSchemaEditorContent
        noSectionSpacing={true}
        processTaskSchema={taskSchema}
        storableSchemas={storableSchemas}
        tagSchemas={tagSchemas}
        taskSchemas={
          // We do not need flowchart type task schemas here because they are not needed for basic task configuration
          allTaskSchemas
            ?.filter((schema) => !isFlowchartType(schema.executionType))
            .map((schema) => ({...schema, flowchartConfig: null})) ?? []
        }
        statusLifecycles={statusLifecycles}
        resultSchemas={resultSchemas}
        isAnyPartOfFlowchartConfigInaccessible={null}
        sectionsToHide={[
          ProcessTaskSchemaEditorContentSections.TASK_FIELDS,
          ProcessTaskSchemaEditorContentSections.OUTPUT,
          ProcessTaskSchemaEditorContentSections.FLOWCHART,
        ]}
        taskDefinitionId={taskDefinitionId}
        afterTaskSchemaUpdate={(newTaskSchema, newTaskDefinitionVersion) => {
          assert(
            newTaskDefinitionVersion != null,
            'Updating a task node definition is expected to return a version'
          );
          cacheUpdatedTaskSchemaAndUpdateNode(
            newTaskSchema,
            selectedNode,
            context.onUpdateTaskDefinitionVersion,
            newTaskDefinitionVersion
          );
          analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.EDITED_TASK_NODE_DEFINITION_FROM_FLOWCHART, {
            actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
            nodeType: buildExecutionNodeTypeIdentifier(
              ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
              newTaskSchema.executionType
            ),
            taskDefinitionVersionId: newTaskDefinitionVersion.id,
            taskSchemaId: newTaskSchema.id,
            executionType: newTaskSchema.executionType,
            flowchartType: context.flowchartEditorConfigurator.flowchartTypeName,
          });
        }}
        locked={props.locked}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  noFieldSelectedText: {
    fontSize: '16px',
    color: Color.GRAY_900,
  },
  taskSchemaEditorContainer: {
    marginTop: 0,
  },
});
